import React from 'react';
import { Trash2, Edit2, Mail, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InsuranceCarrier } from '@shared';
import { CarrierAssignment, CarrierDisplay } from './CarrierSettings';
import type { useBgaManagement } from '@client/hooks/useBgaManagement';

interface BgaCardProps {
  bga: BgaEntry;
  bgaManagement: ReturnType<typeof useBgaManagement>;
  carriers: InsuranceCarrierWithPolicyTypes[];
  onAssignCarrier: (bgaOrganizationId: string, carrierId: InsuranceCarrier) => void;
}

export const BgaCard: React.FC<BgaCardProps> = ({
  bga,
  bgaManagement,
  carriers,
  onAssignCarrier,
}) => {
  const isEditing = bgaManagement.editingBgaId === bga.advisorOrganizationId;
  const nameError = bgaManagement.getFieldError(bga.advisorOrganizationId, 'name');
  const emailError = bgaManagement.getFieldError(bga.advisorOrganizationId, 'contactEmail');
  const canSave = bga.name.trim() && bga.contactEmail.trim() && !nameError && !emailError;
  if (isEditing) {
    return (
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1 space-y-1">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor={`company-${bga.advisorOrganizationId}`}>Company Name *</Label>
                  <Input
                    id={`company-${bga.advisorOrganizationId}`}
                    value={bga.name}
                    onChange={e => bgaManagement.updateBga(bga.advisorOrganizationId, { name: e.target.value })}
                    onBlur={() => bgaManagement.validateField(bga.advisorOrganizationId, 'name')}
                    placeholder="Enter company name"
                    className={nameError ? 'border-red-500' : ''}
                  />
                  {nameError && <p className="text-xs text-red-600 mt-1">{nameError}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`email-${bga.advisorOrganizationId}`}>Email *</Label>
                  <Input
                    id={`email-${bga.advisorOrganizationId}`}
                    type="email"
                    value={bga.contactEmail}
                    onChange={e =>
                      bgaManagement.updateBga(bga.advisorOrganizationId, { contactEmail: e.target.value })
                    }
                    onBlur={() => bgaManagement.validateField(bga.advisorOrganizationId, 'contactEmail')}
                    placeholder="contact@company.com"
                    className={emailError ? 'border-red-500' : ''}
                  />
                  {emailError && <p className="text-xs text-red-600 mt-1">{emailError}</p>}
                </div>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => bgaManagement.removeBga(bga.advisorOrganizationId)}
              className="ml-4 text-gray-400 hover:text-red-600"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <CarrierAssignment
            bga={bga}
            carriers={carriers}
            onAssignCarrier={onAssignCarrier}
            onRemoveCarrier={bgaManagement.removeCarrierFromBga}
            onUpdateCarrierMeta={bgaManagement.updateCarrierMeta}
            isCarrierAssigned={bgaManagement.isCarrierAssigned}
          />
          <div className="flex items-center justify-end gap-2 border-t pt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => bgaManagement.cancelBgaEdit(bga.advisorOrganizationId)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={bgaManagement.saveBga}
              disabled={!canSave}
            >
              Save
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-medium">{bga.name}</h3>
              {bga.contactEmail && (
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <Mail className="h-3 w-3" />
                  {bga.contactEmail}
                </div>
              )}
            </div>
            <CarrierDisplay
              bga={bga}
              carriers={carriers}
            />
          </div>
          <div className="ml-4 flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => bgaManagement.setEditingBgaId(bga.advisorOrganizationId)}
              className="text-gray-600 hover:text-gray-900"
            >
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => bgaManagement.removeBga(bga.advisorOrganizationId)}
              className="text-gray-600 hover:text-red-600"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Loading State Component
export const BgaLoadingState: React.FC = () => (
  <Card>
    <CardContent className="p-4">
      <div className="space-y-4">
        {[1, 2].map(index => (
          <div
            key={index}
            className="animate-pulse"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1 space-y-2">
                <div className="h-5 w-1/3 rounded bg-gray-200"></div>
                <div className="h-4 w-1/2 rounded bg-gray-200"></div>
              </div>
              <div className="flex gap-2">
                <div className="h-8 w-8 rounded bg-gray-200"></div>
                <div className="h-8 w-8 rounded bg-gray-200"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

// Empty State Components
export const EmptyBgaState: React.FC<{ onAddBga: () => void }> = ({ onAddBga }) => (
  <Card>
    <CardContent className="flex flex-col items-center justify-center py-12">
      <h3 className="mb-2 text-lg font-medium">No carrier appointments yet</h3>
      <p className="mb-4 text-center text-sm text-gray-500">
        You haven&apos;t added any BGAs/IMOs or direct appointments yet.
      </p>
      <Button
        type="button"
        onClick={onAddBga}
        variant="outline"
        className="gap-2"
      >
        <Plus className="h-4 w-4" />
        Add carrier appointment
      </Button>
    </CardContent>
  </Card>
);
